import { createContext, useState, useRef, useEffect } from 'react'
import _groupBy from 'lodash/groupBy'
import _max from 'lodash/max'
import qs from 'qs'
import { GET_SLOTS } from 'dataSource/ProviderSearch/routes'
import useApi from 'hooks/useApi'
import { getTimeZoneFormattedDate } from 'utils/common'
import { dateFormats, DayOfWeek } from 'constants/common'
import { sortGeneratedSlots } from 'utils/common'
import {
  getSlotsStartEndDate,
  getCappedExtendedBookingSlotListLimit,
} from 'app/ProviderSearch/utils'
import addDays from 'date-fns/addDays'
import startOfDay from 'date-fns/startOfDay'
import { useSearchParams } from 'next/navigation'
import {
  RETURN_FILTER_KEY_MAPPING,
  LabelAppointmentMapping,
  VIRTUAL_SLOT_ID_PREFIX,
  VIRTUAL_SLOTS_LOAD_ERROR,
} from 'app/ProviderSearch/constants'
import useToast from 'hooks/useToast'

const initialValue: ProviderScheduleDetails = {
  providerSlots: {},
  loading: true,
  error: null,
  nextAvailabilityForProviders: null,
}

type GetProviderSlotsParams = {
  startSlotDate: Date
  timeZone: string
  providerList: Provider[]
  practiceId?: string
  slotListLimit: number
  availabilityFilters?: ProviderFilter[]
  slotsType?: string
  allowSlotsAPICall?: boolean
  skipTimeOfDayFilter?: boolean
  skipDayOfWeekFilter?: boolean
  extendedBookingEnabled?: boolean
  bookingWindowHours?: number
}

const VIRTUAL_SLOT_DURATION_MINUTES = 60

type VirtualSlotResponseSlot = {
  from: string
  to: string
  status?: string
  availabilityType?: string[]
  provider: { id: string }
  practice: { id: string }
}

type MergeVirtualSlotsParams = {
  providerList: Provider[]
  practiceId?: string
  startDate: Date
  endDate: Date
  timeZone: string
  request: ReturnType<typeof useApi>['request']
  timeOfDay?: string[]
  dayOfWeek?: string[]
  type?: string
}

export const mergeVirtualSlotsIntoSchedule = async (
  schedule: NormalizedProviderSchedule,
  {
    providerList,
    practiceId,
    startDate,
    endDate,
    timeZone,
    request,
    timeOfDay,
    dayOfWeek,
    type,
  }: MergeVirtualSlotsParams
): Promise<boolean> => {
  const formatDateKey = (value: string | number | Date): string =>
    getTimeZoneFormattedDate(value, dateFormats.YEAR_MONTH_DAY_FORMAT, timeZone)

  const endDateKey = formatDateKey(endDate)

  const isProviderCoveredThroughEndDate = (provider: Provider): boolean => {
    const datesWithTraditionalSlots = Object.keys(schedule[provider.id] ?? {})
    const latestDateKey = _max(datesWithTraditionalSlots) ?? ''
    return !!latestDateKey && endDateKey <= latestDateKey
  }

  const allProvidersAlreadyCovered = providerList.every(
    isProviderCoveredThroughEndDate
  )

  if (allProvidersAlreadyCovered) {
    return true
  }

  const toVirtualProviderSlot = (
    slot: VirtualSlotResponseSlot
  ): ProviderSlot => ({
    id: `${VIRTUAL_SLOT_ID_PREFIX}${slot.from}`,
    start: slot.from,
    end: slot.to,
    status: slot.status,
    practiceId: slot.practice.id,
    availabilityType: slot.availabilityType,
  })

  const providerIds = providerList.map((provider) => provider.id)
  const practiceIds = providerList.map(
    (provider) => practiceId ?? provider.practice?.id
  )
  const query = qs.stringify(
    {
      providerIds,
      practiceIds,
      startDate: formatDateKey(startDate),
      endDate: endDateKey,
      duration: String(VIRTUAL_SLOT_DURATION_MINUTES),
      ...(!!type && { type }),
      ...(!!timeOfDay?.length && { timeOfDay, timezone: timeZone }),
      ...(!!dayOfWeek?.length && { dayOfWeek, timezone: timeZone }),
    },
    { arrayFormat: 'repeat', skipNulls: true }
  )

  const response = await request.get(`/virtual-slots?${query}`)

  // On a failed fetch keep the traditional schedule untouched instead of
  // treating the empty response as "no virtual slots"; report the failure so
  // the caller does not cache this range as fully fetched.
  if (request.error) {
    return false
  }

  const virtualSlots = (response?.slots ?? []) as VirtualSlotResponseSlot[]
  const virtualSlotsByProvider = _groupBy(
    virtualSlots,
    (slot) => slot.provider.id
  )

  providerList.forEach((provider) => {
    const providerVirtualSlots = virtualSlotsByProvider[provider.id] ?? []
    const tradDateSlots = schedule[provider.id] ?? {}
    const mergedDateSlots: DateSlot = { ...tradDateSlots }
    const virtualSlotsByDate = _groupBy(providerVirtualSlots, (slot) =>
      formatDateKey(slot.from)
    )

    Object.keys(virtualSlotsByDate).forEach((dateKey) => {
      if (mergedDateSlots[dateKey]) {
        return
      }

      mergedDateSlots[dateKey] = virtualSlotsByDate[dateKey].map(
        toVirtualProviderSlot
      )
    })

    schedule[provider.id] = sortGeneratedSlots(mergedDateSlots)
  })

  return true
}

export const getProviderSlots = ({
  startSlotDate,
  providerList,
  practiceId,
  timeZone,
  slotListLimit,
  availabilityFilters = null,
  slotsType = '',
  allowSlotsAPICall = true,
  skipTimeOfDayFilter = false,
  skipDayOfWeekFilter = false,
  extendedBookingEnabled = false,
  bookingWindowHours,
}: GetProviderSlotsParams): ProviderScheduleDetails => {
  const lastRequestRef = useRef<string>(null)
  const fetchedRangeRef = useRef<{
    start: Date
    end: Date
    providerKey: string
    originalStartSlotDate: Date
    originalSlotListLimit: number
    filterSignature: string
    extendedBookingEnabled: boolean
  }>(null)
  const searchParams = useSearchParams()

  const filterSignature = [
    skipTimeOfDayFilter
      ? ''
      : searchParams.getAll('timeOfDay[]').sort().join(','),
    skipDayOfWeekFilter
      ? ''
      : searchParams.getAll('dayOfWeek[]').sort().join(','),
    searchParams
      .getAll(`${RETURN_FILTER_KEY_MAPPING.availability_type}[]`)
      .sort()
      .join(','),
    timeZone ?? '',
  ].join('|')
  const [
    providerSlots,
    setProviderSlots,
  ] = useState<NormalizedProviderSchedule>(initialValue.providerSlots)
  const [
    nextAvailabilityForProviders,
    setNextAvailability,
  ] = useState<NextAvailabilityMap>(null)
  /**
   * Using a state instead of request.loading because noticed on dev
   * There seems to be a delay to initiate the api call so a pause can be noticed in between
   * the slots api call being made and its loader showing up
   * this is not noticed on qa
   */
  const [loading, setLoading] = useState<boolean>(initialValue.loading)
  const { error, request } = useApi()
  const { showError } = useToast()

  const fetchProviderSlots = async (requestRef: string) => {
    setLoading(true)
    const generatedProviderSlots = {}
    const nextAvailabilityMap: NextAvailabilityMap = {}
    const formattedQuery: Record<string, string | string[]> = {}
    searchParams.forEach((value, key) => {
      if (formattedQuery[key]) {
        if (Array.isArray(formattedQuery[key])) {
          const formattedQueryValue = formattedQuery[key] as string[]
          formattedQueryValue.push(value)
        } else {
          formattedQuery[key] = [formattedQuery[key] as string, value]
        }
      } else {
        formattedQuery[key] = value
      }
    })
    const dayOfWeekFromQuery =
      formattedQuery['dayOfWeek[]'] ?? formattedQuery.dayOfWeek
    const dayOfWeekForRange =
      !skipDayOfWeekFilter && dayOfWeekFromQuery
        ? ((Array.isArray(dayOfWeekFromQuery)
            ? dayOfWeekFromQuery
            : [dayOfWeekFromQuery]) as DayOfWeek[])
        : undefined
    const timeOfDayFromQuery =
      formattedQuery['timeOfDay[]'] ?? formattedQuery.timeOfDay
    const timeOfDayForRange =
      !skipTimeOfDayFilter && timeOfDayFromQuery
        ? Array.isArray(timeOfDayFromQuery)
          ? timeOfDayFromQuery
          : [timeOfDayFromQuery]
        : undefined
    const cappedSlotListLimit = extendedBookingEnabled
      ? getCappedExtendedBookingSlotListLimit(
          startSlotDate,
          slotListLimit,
          timeZone
        )
      : slotListLimit

    const { startDate, endDate } = getSlotsStartEndDate(
      startSlotDate,
      cappedSlotListLimit,
      timeZone,
      dayOfWeekForRange,
      bookingWindowHours,
      extendedBookingEnabled
    )

    let type = slotsType

    const availabilityTypeFromQuery =
      formattedQuery[`${RETURN_FILTER_KEY_MAPPING.availability_type}[]`]

    if (availabilityTypeFromQuery && availabilityFilters) {
      // Fetch the label for the availabilityType id in query
      const availabilityLabelFromId = availabilityFilters.find(
        (filter) => filter.id === availabilityTypeFromQuery
      ).value

      // Fetch the enum value for the same availability  label
      const availabilityValueFromLabel =
        LabelAppointmentMapping[availabilityLabelFromId]

      type = availabilityValueFromLabel
    }

    // use-http automatically converst date object to utc time
    const slots = await request.post(GET_SLOTS, {
      start: startDate,
      end: endDate,
      for: providerList.map((provider) => ({
        providerId: provider.id,
        practiceId: practiceId ?? provider?.practice?.id,
      })),
      ...(timeOfDayForRange && {
        timeOfDay: timeOfDayForRange,
        timezone: timeZone,
      }),
      ...(dayOfWeekForRange && {
        dayOfWeek: dayOfWeekForRange,
        timezone: timeZone,
      }),
      ...(!!type && { type }),
    })

    if (lastRequestRef.current !== requestRef) {
      return
    }

    // Do not call virtual-slots when traditional slots failed — the window
    // (start/end) may be wrong/incomplete and we should not merge into it.
    if (request.error) {
      setLoading(false)
      return
    }

    const groupedSlots = _groupBy(slots, (slot) => slot.providerId)
    providerList?.map((provider) => {
      const providerSlots = groupedSlots[provider.id]

      if (providerSlots?.length) {
        const groupedDateSlots: DateSlot = _groupBy(
          providerSlots[0]?.slots,
          (slot) =>
            getTimeZoneFormattedDate(
              slot.start,
              dateFormats.YEAR_MONTH_DAY_FORMAT,
              timeZone
            )
        )
        const sortedGroupedDateSlots = sortGeneratedSlots(groupedDateSlots)
        generatedProviderSlots[provider.id] = sortedGroupedDateSlots
        //creating next availability map for providers
        nextAvailabilityMap[provider.id] =
          providerSlots?.[0]?.nextAvailableSlot ?? null
      }
    })

    let virtualSlotsFetched = true

    if (extendedBookingEnabled) {
      try {
        virtualSlotsFetched = await mergeVirtualSlotsIntoSchedule(
          generatedProviderSlots,
          {
            providerList,
            practiceId,
            startDate,
            endDate,
            timeZone,
            request,
            timeOfDay: timeOfDayForRange,
            dayOfWeek: dayOfWeekForRange,
            type,
          }
        )
      } catch {
        virtualSlotsFetched = false
      }

      if (lastRequestRef.current !== requestRef) {
        return
      }

      if (!virtualSlotsFetched) {
        showError(VIRTUAL_SLOTS_LOAD_ERROR)
      }
    }

    // Don't cache a range whose virtual slots failed to load, so navigating
    // back into it retries instead of serving an incomplete schedule.
    fetchedRangeRef.current = virtualSlotsFetched
      ? {
          start: startDate,
          end: endDate,
          providerKey: providerList
            .map((p) => p.id)
            .sort()
            .join(','),
          originalStartSlotDate: startOfDay(startSlotDate),
          originalSlotListLimit: cappedSlotListLimit,
          filterSignature,
          extendedBookingEnabled,
        }
      : null
    setNextAvailability(nextAvailabilityMap)
    setProviderSlots(generatedProviderSlots)
    setLoading(false)
  }

  const prevBookingWindowHoursRef = useRef<number>(bookingWindowHours)
  useEffect(() => {
    if (
      prevBookingWindowHoursRef.current !== bookingWindowHours &&
      bookingWindowHours !== undefined
    ) {
      fetchedRangeRef.current = null
      prevBookingWindowHoursRef.current = bookingWindowHours
    }
  }, [bookingWindowHours])

  useEffect(() => {
    if (startSlotDate && providerList?.length && allowSlotsAPICall) {
      const fetched = fetchedRangeRef.current
      const currentProviderKey = providerList
        .map((p) => p.id)
        .sort()
        .join(',')
      const cappedSlotListLimit = extendedBookingEnabled
        ? getCappedExtendedBookingSlotListLimit(
            startSlotDate,
            slotListLimit,
            timeZone
          )
        : slotListLimit

      if (
        fetched &&
        fetched.providerKey === currentProviderKey &&
        fetched.filterSignature === filterSignature &&
        fetched.extendedBookingEnabled === extendedBookingEnabled
      ) {
        const normalizedStartSlotDate = startOfDay(startSlotDate)
        // Skip only when the full needed window (start → start+limit) is inside
        // the previously fetched range. Checking start alone wrongly skips when
        // Jump-to-date / chevron navigation slides the window so the start
        // overlaps the old range but the end extends past it (e.g. fetched
        // Sep 14–18, then navigate to Sep 17–21).
        const neededEndSlotDate = addDays(
          normalizedStartSlotDate,
          cappedSlotListLimit
        )
        const fetchedEndSlotDate = addDays(
          fetched.originalStartSlotDate,
          fetched.originalSlotListLimit
        )

        if (
          normalizedStartSlotDate >= fetched.originalStartSlotDate &&
          neededEndSlotDate <= fetchedEndSlotDate
        ) {
          return
        }
      }

      const newRequestTimestampRef = new Date().getTime().toString()
      lastRequestRef.current = newRequestTimestampRef
      fetchProviderSlots(newRequestTimestampRef)
    }
  }, [
    startSlotDate,
    providerList,
    slotListLimit,
    allowSlotsAPICall,
    filterSignature,
    extendedBookingEnabled,
    bookingWindowHours,
    timeZone,
  ])

  return {
    providerSlots,
    nextAvailabilityForProviders,
    loading,
    error,
  }
}

const ProviderSlotContext = createContext(initialValue)
export default ProviderSlotContext
